import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FaAddressBook,
  FaBan,
  FaBullhorn,
  FaCalendarCheck,
  FaCalendarTimes,
  FaCheckCircle,
  FaClipboardCheck,
  FaClock,
  FaComments,
  FaExclamationCircle,
  FaExclamationTriangle,
  FaInbox,
  FaPhoneAlt,
  FaProjectDiagram,
  FaRupeeSign,
  FaTasks,
  FaUserClock,
  FaUserPlus,
  FaUsers,
  FaWallet,
} from "react-icons/fa";
import axiosInstance from "../components/api/axiosInstance";
import { getAllowedSidebarPages, getCurrentUserRole } from "../utils/roleAccess";
import { getAuthItem } from "../utils/authStorage";
import { Stat } from "./dm/components/common";
import { staffWorkApi, toISODate } from "./work/workApi";
import "./dm/dm.css";
import "./work/work.css";

/**
 * High-level overview only. Day-to-day lists (my tasks, posts, reviews, calendar) live under Work;
 * every number here links to the page with the details.
 */

const LEAD_MODULE_KEYS = ["business", "realestate", "training", "job", "internship"];
const ORG_WIDE_ROLES = ["admin", "administrator", "hr", "human resources", "manager"];

const toNum = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

function sumModules(modules, field) {
  return (Array.isArray(modules) ? modules : [])
    .filter((m) => LEAD_MODULE_KEYS.includes(m?.key))
    .reduce((total, m) => total + toNum(m?.[field]), 0);
}

function leadsState(status, extra = {}) {
  return {
    digixFilter: { status, datePreset: "all", createdFrom: "", createdTo: "", nextFollowupDate: "", ...extra },
  };
}

function Tile({ label, value, icon, tone, onClick, title }) {
  return (
    <button type="button" className="wk-tile-btn" onClick={onClick} title={title || `Open ${label}`}>
      <Stat label={label} value={value ?? "—"} icon={icon} tone={tone} />
    </button>
  );
}

function Section({ title, link, linkLabel, children }) {
  return (
    <section className="dmp-card" aria-label={title}>
      <h2 className="dmp-card__title">
        {title}
        {link ? (
          <Link to={link} className="dmp-link" style={{ fontSize: 13, fontWeight: 600 }}>
            {linkLabel || "Open"}
          </Link>
        ) : null}
      </h2>
      <div className="dmp-stats">{children}</div>
    </section>
  );
}

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}

function userFirstName() {
  try {
    const raw = getAuthItem("user");
    return raw ? JSON.parse(raw)?.first_name || "" : "";
  } catch {
    return "";
  }
}

function Dashboard() {
  const navigate = useNavigate();
  const role = (getCurrentUserRole() || "").toLowerCase();
  const allowed = useMemo(() => new Set(getAllowedSidebarPages(role)), [role]);
  const orgWide = ORG_WIDE_ROLES.includes(role);

  const [counts, setCounts] = useState(null);
  const [mine, setMine] = useState(null);
  const [team, setTeam] = useState(null);
  const [reviews, setReviews] = useState(null);
  const [posts, setPosts] = useState(null);
  const [comments, setComments] = useState(null);

  const showMarketing = allowed.has("DMProjects") || allowed.has("DigitalMarketing");
  const canFollowUps = allowed.has("WorkFollowUps");
  const canReview = allowed.has("ReviewInbox");
  const canTeam = allowed.has("TeamBoard");

  useEffect(() => {
    let alive = true;
    const keep = (setter) => (value) => alive && setter(value);
    axiosInstance.get("/dashboard/counts").then((res) => keep(setCounts)(res?.data || {})).catch(() => keep(setCounts)({}));
    staffWorkApi.myWork().then(keep(setMine)).catch(() => keep(setMine)(null));
    if (canTeam) staffWorkApi.teamWork().then(keep(setTeam)).catch(() => keep(setTeam)(null));
    if (canReview) staffWorkApi.reviewInbox().then(keep(setReviews)).catch(() => keep(setReviews)(null));
    if (showMarketing) {
      const today = new Date();
      const inAWeek = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 6);
      staffWorkApi
        .calendar({ date_from: toISODate(today), date_to: toISODate(inAWeek) })
        .then(keep(setPosts))
        .catch(() => keep(setPosts)(null));
      staffWorkApi.comments({ status: "New" }).then(keep(setComments)).catch(() => keep(setComments)(null));
    }
    return () => {
      alive = false;
    };
  }, [showMarketing, canTeam, canReview]);

  const c = counts || {};
  const projects = c.projects || {};
  const tasks = c.tasks || {};
  const issues = c.issues || {};
  const myIssues = issues.assigned || {};
  const leads = c.leads || {};
  const followups = c.followups || {};
  const payments = c.payments || {};
  const go = (path, state) => () => navigate(path, state ? { state } : undefined);
  const loaded = counts !== null;
  const v = (n) => (loaded ? toNum(n) : null);

  const myCounts = mine?.counts || {};
  const managesPeople = (team?.people || []).length > 1;

  return (
    <div className="dmp wk">
      <div className="dash-greet">
        <h2 className="dash-greet__title">
          {greeting()}
          {userFirstName() ? `, ${userFirstName()}` : ""}
        </h2>
        <div className="dmp-actions dash-greet__actions">
        {canFollowUps ? (
          <Link className="dmp-btn dmp-btn--ghost" to="/work/follow-ups">
            <FaTasks aria-hidden="true" /> Follow-ups
          </Link>
        ) : null}
        {showMarketing ? (
          <Link className="dmp-btn dmp-btn--ghost" to="/digital-marketing">
            <FaCalendarCheck aria-hidden="true" /> Posting calendar
          </Link>
        ) : null}
        <Link className="dmp-btn dmp-btn--new" to="/my-work">
          <FaClipboardCheck aria-hidden="true" /> My work
        </Link>
        </div>
      </div>

      {mine?.needs_update > 0 ? (
        <div className="wk-banner" role="status">
          <span>
            <strong>{mine.needs_update}</strong> of your items have no status update today.
          </span>
          <Link className="dmp-btn" to="/my-work?update=1">
            Update my day
          </Link>
        </div>
      ) : null}

      <Section
        title="Work"
        link={canFollowUps ? "/work/follow-ups" : "/my-work"}
        linkLabel={canFollowUps ? "Open follow-ups" : "Open my work"}
      >
        <Tile label="My late items" value={mine ? myCounts.overdue : null} icon={FaExclamationTriangle} tone="rose" onClick={go("/my-work")} />
        <Tile label="My items due today" value={mine ? myCounts.today : null} icon={FaClock} tone="amber" onClick={go("/my-work")} />
        {canReview ? (
          <Tile label="Waiting for my review" value={reviews ? reviews.count : null} icon={FaInbox} tone="violet" onClick={go("/work/review")} />
        ) : null}
        {managesPeople ? (
          <>
            <Tile
              label="Team open items"
              value={team.totals.open}
              icon={FaUsers}
              tone="blue"
              onClick={go(canFollowUps ? "/work/follow-ups?tab=deliverables" : "/work/team")}
            />
            <Tile
              label="Team late"
              value={team.totals.overdue}
              icon={FaExclamationTriangle}
              tone="rose"
              onClick={go(canFollowUps ? "/work/follow-ups?tab=deliverables" : "/work/team")}
            />
            <Tile label="Team stuck" value={team.totals.blocked} icon={FaBan} tone="amber" onClick={go("/work/team")} />
            <Tile label="No update today" value={team.totals.missing_updates} icon={FaUserClock} tone="amber" onClick={go("/work/team")} />
          </>
        ) : null}
      </Section>

      {showMarketing ? (
        <Section title="Marketing this week" link="/digital-marketing" linkLabel="Open posting calendar">
          <Tile label="Posts planned" value={posts ? posts.summary.planned : null} icon={FaBullhorn} tone="blue" onClick={go("/digital-marketing")} />
          <Tile label="Posted" value={posts ? posts.summary.posted : null} icon={FaCheckCircle} tone="green" onClick={go("/digital-marketing")} />
          <Tile label="Late posts" value={posts ? posts.summary.overdue : null} icon={FaExclamationTriangle} tone="rose" onClick={go("/digital-marketing")} />
          <Tile label="Days with nothing planned" value={posts ? posts.gaps.length : null} icon={FaCalendarTimes} tone="amber" onClick={go("/digital-marketing")} />
          <Tile label="Comments to answer" value={comments ? comments.new_count : null} icon={FaComments} tone="violet" onClick={go("/work/comments")} />        </Section>
      ) : null}

      {allowed.has("DigixLeads") ? (
        <Section title="Leads" link="/work/follow-ups?tab=leads" linkLabel="Open leads pipeline">
          <Tile label="New leads" value={v(sumModules(leads.modules, "newLeads"))} icon={FaUserPlus} tone="blue" onClick={go("/business-leads", leadsState("New"))} />
          <Tile
            label="New today"
            value={v(sumModules(leads.modules, "newLeadsToday"))}
            icon={FaUserPlus}
            tone="green"
            onClick={go("/business-leads", leadsState("New", { datePreset: "today", createdFrom: toISODate(), createdTo: toISODate() }))}
          />
          <Tile label="Waiting for confirmation" value={v(sumModules(leads.modules, "waiting"))} icon={FaClock} tone="amber" onClick={go("/business-leads", leadsState("Waiting for Confirmation"))} />
          <Tile
            label="Follow-ups today"
            value={v(followups.today)}
            icon={FaPhoneAlt}
            tone="indigo"
            onClick={go("/business-leads", leadsState("All Statuses", { nextFollowupDate: toISODate() }))}
          />
          <Tile label="Follow-ups overdue" value={v(followups.overdue)} icon={FaExclamationCircle} tone="rose" onClick={go("/business-leads", leadsState("Followup"))} />
          <Tile label="Total leads" value={v(leads.total)} icon={FaAddressBook} tone="cyan" onClick={go("/business-leads", leadsState("All Statuses"))} />
        </Section>
      ) : null}

      {allowed.has("Projects") || allowed.has("Issues") ? (
        <Section title={orgWide ? "Projects & delivery" : "My projects & delivery"} link="/projects" linkLabel="Open projects">
          {allowed.has("Projects") ? (
            <>
              <Tile
                label={orgWide ? "Active projects" : "My projects"}
                value={v(orgWide ? projects.active : projects.total)}
                icon={FaProjectDiagram}
                tone="blue"
                onClick={go("/projects", { projectManagementTab: "Projects", dashboardProjectStatusPreset: orgWide ? "active" : "assigned" })}
              />
              {orgWide ? (
                <Tile
                  label="New projects"
                  value={v(projects.new)}
                  icon={FaProjectDiagram}
                  tone="indigo"
                  onClick={go("/projects", { projectManagementTab: "Projects", dashboardProjectStatusPreset: "new" })}
                />
              ) : null}
              <Tile
                label={orgWide ? "Tasks to do" : "My tasks to do"}
                value={v(orgWide ? tasks.todo : tasks.assignedTodo)}
                icon={FaTasks}
                tone="amber"
                onClick={go("/tasks", { dashboardTaskStatusPreset: "todo" })}
              />
              <Tile
                label={orgWide ? "Tasks in progress" : "My tasks in progress"}
                value={v(orgWide ? tasks.inProgress : tasks.assignedInProgress)}
                icon={FaTasks}
                tone="cyan"
                onClick={go("/tasks", { dashboardTaskStatusPreset: "inProgress" })}
              />
            </>
          ) : null}
          {allowed.has("Issues") ? (
            <>
              <Tile
                label={orgWide ? "Open issues" : "My open issues"}
                value={v(orgWide ? issues.open : myIssues.open)}
                icon={FaExclamationCircle}
                tone="rose"
                onClick={go("/issues")}
              />
              <Tile
                label={orgWide ? "Reopened issues" : "My reopened issues"}
                value={v(orgWide ? issues.reopened : myIssues.reopened)}
                icon={FaExclamationTriangle}
                tone="amber"
                onClick={go("/issues")}
              />
            </>
          ) : null}
        </Section>
      ) : null}

      {allowed.has("Tenants") || allowed.has("FinanceManagement") ? (
        <Section title="Clients & finance">
          {allowed.has("Tenants") ? (
            <Tile label="Clients" value={v((c.customers || {}).total)} icon={FaAddressBook} tone="blue" onClick={go("/tenants")} />
          ) : null}
          {allowed.has("FinanceManagement") ? (
            <>
              <Tile label="Payments due" value={v(payments.due)} icon={FaWallet} tone="rose" onClick={go("/finance/receivables")} />
              <Tile label="Payments received" value={v(payments.completed)} icon={FaRupeeSign} tone="green" onClick={go("/finance/payments-received")} />
            </>
          ) : null}
        </Section>
      ) : null}
    </div>
  );
}

export default Dashboard;
